import type { IValueMapping } from '@atlas/types'
import { MappingSystem } from '@atlas/types'
import { SectionCard } from '@atlas/ui-components'

/**
 * Everything ATLAS records about a brand or product that the rest of the page does not
 * show: its identifiers, its Veeva links, and the raw `meta` behind both.
 *
 * Deliberately a READ. The taxonomy load writes most of this — `veeva_object_type`,
 * `veeva_alias_ids`, `veeva_matched` — and a reader debugging why a value resolves the
 * way it does needs to see it as stored, not as summarised. Editing belongs on the
 * brand-edit page, against the fields that own each value.
 */

/** A Vault link, from the two halves the mapping and the value's meta hold between them. */
export const veevaUrlFor = (objectType: string | undefined, id: string): string | null =>
	objectType ? `https://ahpromo.veevavault.com/ui/#object/${objectType}/${id}` : null

const SYSTEM_LABEL: Record<string, string> = {
	[MappingSystem.VEEVA]: 'Veeva',
	[MappingSystem.WORKFRONT]: 'Workfront',
	[MappingSystem.TAXONOMY_TITLE]: 'Market name',
}

export interface MetaTabProps {
	/** What the page is about, for the empty states: `brand` or `product`. */
	subject: 'brand' | 'product'
	code: string
	valueId: string
	/** The catalog row's own `meta` — taglines, links, pillar, the backfill flag. */
	meta: Record<string, unknown>
	/** The axis value's `meta`, which is where the taxonomy load writes Veeva provenance. */
	valueMeta?: Record<string, unknown>
	/** Every mapping on the value, across systems — the raw rows, unfiltered. */
	mappings: IValueMapping[]
	/**
	 * An action for the Identity card — the merge control, where the page offers one.
	 *
	 * Passed in rather than imported so this tab stays a READ of what ATLAS records: the
	 * card is where a reader confirms two pages are the same thing, and what they may DO
	 * about it belongs to the page, not to the record.
	 */
	identityAction?: React.ReactNode
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="flex items-baseline justify-between gap-4 border-ds-border border-b py-2 last:border-0">
			<span className="shrink-0 text-ds-text-muted text-xs">{label}</span>
			<span className="min-w-0 text-right text-ds-text text-sm">{children}</span>
		</div>
	)
}

/** A value rendered for reading: scalars plainly, anything structured as JSON. */
function MetaValue({ value }: { value: unknown }) {
	if (value === null || value === undefined) return <span className="text-ds-text-subtle">—</span>
	if (typeof value === 'boolean') return <span className="font-mono text-xs">{value ? 'true' : 'false'}</span>
	if (typeof value === 'string' || typeof value === 'number')
		return <span className="break-words">{String(value)}</span>
	return (
		<pre className="overflow-x-auto whitespace-pre-wrap text-left font-mono text-ds-text-muted text-xs">
			{JSON.stringify(value, null, 2)}
		</pre>
	)
}

/**
 * The Veeva object this value IS, and the link to it.
 *
 * Its own component because the empty state carries real meaning that differs by subject:
 * a brand has no Veeva object because none exists to have, while a product without one was
 * simply never in the export.
 */
function VeevaCard({
	subject,
	veeva,
	objectType,
	aliasIds,
}: {
	subject: 'brand' | 'product'
	veeva: IValueMapping[]
	objectType: string | undefined
	aliasIds: string[]
}) {
	return (
		<SectionCard
			title="Veeva"
			action={
				// One link, not a list: the primary id is the object this value IS. The
				// aliases below are other ids that resolve to the same thing.
				veeva[0] && veevaUrlFor(objectType, veeva[0].externalCode) ? (
					<a
						className="text-ds-info text-sm hover:underline"
						href={veevaUrlFor(objectType, veeva[0].externalCode) ?? undefined}
						target="_blank"
						rel="noreferrer noopener"
					>
						Open in Vault ↗
					</a>
				) : undefined
			}
		>
			{veeva.length === 0 ? (
				<p className="text-ds-text-muted text-sm">
					No Veeva object is mapped to this {subject}.{' '}
					{subject === 'brand'
						? 'Expected: the export’s brand column is plain text, so no brand has a Veeva object to link to.'
						: 'This product was not ingested from the Veeva export, so it has no object to link to.'}
				</p>
			) : (
				<>
					<Row label="Object id">
						<span className="font-mono text-xs">{veeva[0]?.externalCode}</span>
					</Row>
					<Row label="Object type">
						<MetaValue value={objectType ?? null} />
					</Row>
					{aliasIds.length > 0 && (
						<Row label={`Also known as (${aliasIds.length})`}>
							<span className="font-mono text-xs">{aliasIds.join(', ')}</span>
						</Row>
					)}
				</>
			)}
		</SectionCard>
	)
}

export function MetaTab({ subject, code, valueId, meta, valueMeta = {}, mappings, identityAction }: MetaTabProps) {
	const veeva = mappings.filter((m) => m.system === MappingSystem.VEEVA)
	const objectType = typeof valueMeta.veeva_object_type === 'string' ? valueMeta.veeva_object_type : undefined
	const aliasIds = Array.isArray(valueMeta.veeva_alias_ids) ? (valueMeta.veeva_alias_ids as string[]) : []
	const other = mappings.filter((m) => m.system !== MappingSystem.VEEVA)
	const metaEntries = Object.entries(meta)
	const valueMetaEntries = Object.entries(valueMeta)

	return (
		<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
			<SectionCard
				title="Identity"
				// Whatever the page offers here sits beside the identity it changes: this card is
				// where a reader confirms two pages are the same thing.
				action={identityAction}
			>
				<Row label="Code">
					<span className="font-mono text-xs">{code}</span>
				</Row>
				<Row label="Axis value">
					<span className="font-mono text-xs">{valueId}</span>
				</Row>
				<Row label="Matched in Veeva">
					<MetaValue value={valueMeta.veeva_matched ?? false} />
				</Row>
				{/* A separate fact from the one above. A brand is loaded FROM the export and
				    still has no Veeva object, because the export's brand column is plain text —
				    so "not matched" must not be read as "not from Veeva". */}
				{valueMeta.loaded_from_excel === true && (
					<Row label="Loaded from the Veeva export">
						<MetaValue value={valueMeta.loaded_from_excel_name ?? true} />
					</Row>
				)}
			</SectionCard>

			<VeevaCard subject={subject} veeva={veeva} objectType={objectType} aliasIds={aliasIds} />

			<SectionCard title="Stored fields">
				{metaEntries.length === 0 ? (
					<p className="text-ds-text-muted text-sm">Nothing recorded on this {subject} yet.</p>
				) : (
					metaEntries.map(([key, value]) => (
						<Row key={key} label={key}>
							<MetaValue value={value} />
						</Row>
					))
				)}
			</SectionCard>

			<SectionCard title="Taxonomy fields">
				{valueMetaEntries.length === 0 ? (
					<p className="text-ds-text-muted text-sm">The taxonomy records nothing beyond this {subject}'s code.</p>
				) : (
					valueMetaEntries.map(([key, value]) => (
						<Row key={key} label={key}>
							<MetaValue value={value} />
						</Row>
					))
				)}
			</SectionCard>

			{other.length > 0 && (
				<SectionCard title="Other mappings" className="lg:col-span-2">
					{other.map((m) => (
						<Row key={`${m.system}:${m.geoValueId ?? 'worldwide'}`} label={SYSTEM_LABEL[m.system] ?? m.system}>
							<span className="break-words">
								{m.externalCode}
								{m.geoValueId ? <span className="ml-2 text-ds-text-muted text-xs">scoped</span> : null}
							</span>
						</Row>
					))}
				</SectionCard>
			)}
		</div>
	)
}
